import { action } from '@ember/object';
import { cached, tracked } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import sortBy from 'lodash-es/sortBy';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import BaseController from 'uplisting-frontend/pods/base/controller';

export interface IFallback {
  changeoversWait: boolean;
}

export interface IPropertyRow {
  property: ModelRegistry['property'];
  rules: ModelRegistry['assignment-rule'][];
  fallback: IFallback | null;
}

export default class ActionAssignmentRulesController extends BaseController {
  @service('repositories/assignment-rule')
  assignmentRuleRepository!: Services['repositories/assignment-rule'];

  @service('repositories/property')
  propertyRepository!: Services['repositories/property'];

  @tracked editingRule: ModelRegistry['assignment-rule'] | null = null;
  @tracked query = '';
  @tracked rulesFilter: 'all' | 'with_rules' | 'without_rules' = 'all';

  @cached
  get properties(): ModelRegistry['property'][] {
    return this.propertyRepository.peekAll();
  }

  @cached
  get rulesSorted(): ModelRegistry['assignment-rule'][] {
    return sortBy(this.assignmentRuleRepository.peekAll(), [
      (rule) => this.propertyName(rule),
      'kind',
    ]);
  }

  @cached
  get isCreateNewDisabled(): boolean {
    return this.rulesSorted.some((rule) => !rule.isPersisted);
  }

  // The page answers "who cleans this property" for the whole portfolio:
  // every property appears, with its rules or with the behaviour it falls
  // back to (the automatic tickbox's open pool, or manual assignment).
  @cached
  get propertyRows(): IPropertyRow[] {
    const sorted = [...this.properties].sort((a, b) =>
      (a.nickname ?? '').localeCompare(b.nickname ?? ''),
    );

    return sorted.map((property) => {
      const rules = this.rulesSorted.filter(
        (rule) => rule.isPersisted && rule.propertyId === Number(property.id),
      );

      return {
        property,
        rules,
        fallback: rules.length ? null : this.fallbackFor(property),
      };
    });
  }

  @action
  propertyName(rule: ModelRegistry['assignment-rule']): string {
    const property = this.properties.find(
      (record) => Number(record.id) === rule.propertyId,
    );

    return property?.nickname ?? '';
  }

  @action
  rotationSummary(rule: ModelRegistry['assignment-rule']): string {
    return rule.sortedRotation
      .map((entry) => entry.name ?? `#${entry.user_id}`)
      .join(', ');
  }

  @cached
  get filteredPropertyRows(): IPropertyRow[] {
    const needle = this.query.trim().toLowerCase();

    return this.propertyRows.filter((row) => {
      if (
        needle &&
        !(row.property.nickname ?? '').toLowerCase().includes(needle)
      ) {
        return false;
      }

      if (this.rulesFilter === 'with_rules') {
        return row.rules.length > 0;
      }

      if (this.rulesFilter === 'without_rules') {
        return row.rules.length === 0;
      }

      return true;
    });
  }

  @cached
  get isFiltered(): boolean {
    return this.query.trim().length > 0 || this.rulesFilter !== 'all';
  }

  @cached
  get editingRules(): ModelRegistry['assignment-rule'][] {
    return this.editingRule ? [this.editingRule] : [];
  }

  @action
  handleSearch(event: Event): void {
    this.query = (event.target as HTMLInputElement).value;
  }

  @action
  handleSetRulesFilter(value: 'all' | 'with_rules' | 'without_rules'): void {
    this.rulesFilter = value;
  }

  @action
  handleCreateRule(): void {
    this.editingRule = this.assignmentRuleRepository.createRecord();
  }

  @action
  handleCreateRuleFor(property: ModelRegistry['property']): void {
    this.editingRule = this.assignmentRuleRepository.createRecord({
      propertyId: Number(property.id),
    });
  }

  fallbackFor(property: ModelRegistry['property']): IFallback {
    const setting = property.changeoverSetting;

    return { changeoversWait: !!setting?.enabled && !setting.automatic };
  }

  @action
  handleEditRule(rule: ModelRegistry['assignment-rule']): void {
    this.editingRule = rule;
  }

  @action
  handleOpenExisting(rule: ModelRegistry['assignment-rule']): void {
    const draft = this.editingRule;

    this.editingRule = rule;

    if (draft && !draft.isPersisted) {
      draft.unloadRecord();
    }

    this.notifications.info('action.assignment_rules.existing_rule_opened');
  }

  @action
  handleClosePanel(): void {
    const rule = this.editingRule;

    this.editingRule = null;

    if (rule && !rule.isPersisted) {
      rule.unloadRecord();
    } else {
      rule?.rollbackAttributes();
    }
  }
}
